import { formatHumanName, parseReference } from "@medplum/core";
import { CareTeam, Communication, Patient, Practitioner, Provenance } from "@medplum/fhirtypes";

import { CommunicationInbox, compareInboxByLatestMessage } from "@/store/slices/inbox";

import { MessagePreview } from "../types/inbox";

/**
 * Builds an inbox row from a thread Communication.
 *
 * @param communication - the thread.
 * @param details - the recipient and client, plus the topic, status, time, unread count and last message preview to show.
 * @returns the inbox row.
 */
export function createCommunicationInbox(
  communication: Communication,
  {
    recipient,
    client,
    topic,
    status,
    time,
    unread = 0,
    lastMessagePreview,
  }: {
    recipient: Patient | Practitioner | CareTeam;
    client?: Patient;
    topic?: string;
    status?: string;
    time?: string;
    unread?: number;
    lastMessagePreview?: MessagePreview;
  },
): CommunicationInbox {
  const recipientName =
    recipient.resourceType === "CareTeam"
      ? (recipient.name ?? "Unnamed team")
      : formatHumanName(
          recipient.name?.find((name) => name.use === "official") ?? recipient.name?.[0],
        );

  return {
    id: communication.id!,
    name: recipientName,
    topic: topic ?? communication.topic?.text,
    status: status ?? communication.status,
    client,
    time,
    recipient,
    unread,
    lastMessagePreview,
  };
}

/**
 * Lists the threads covered by read receipts. A receipt targets the message read first and its
 * thread second; a receipt without a thread is skipped.
 *
 * @param provenances - read receipts on thread messages.
 * @returns the ids of the threads read.
 */
export function getReadCommunicationIds(provenances: Provenance[]): Set<string> {
  return provenances.reduce((readIds, provenance) => {
    const threadTarget = provenance.target?.[1];
    if (threadTarget?.reference) readIds.add(parseReference(threadTarget)[1]);
    return readIds;
  }, new Set<string>());
}

/**
 * Resets the unread count of the inbox entries that read receipts cover.
 *
 * @param inbox - inbox entries by thread id.
 * @param provenances - read receipts on thread messages.
 * @returns only the covered entries, with no unread messages.
 */
export function updateCommunicationInboxList(
  inbox: Record<string, CommunicationInbox>,
  provenances: Provenance[],
): CommunicationInbox[] {
  return Array.from(getReadCommunicationIds(provenances)).reduce<CommunicationInbox[]>(
    (updatedInbox, communicationId) => {
      const item = inbox[communicationId];
      if (item) updatedInbox.push({ ...item, unread: 0 });
      return updatedInbox;
    },
    [],
  );
}

/**
 * Resets the unread count of the threads that read receipts cover.
 *
 * @param threads - the loaded threads.
 * @param provenances - read receipts on thread messages.
 * @returns the threads with the read ones cleared, or the same array when none of them changes.
 */
export function applyReadReceipts(
  threads: CommunicationInbox[],
  provenances: Provenance[],
): CommunicationInbox[] {
  const readThreadIds = getReadCommunicationIds(provenances);
  const isUnreadAndRead = (thread: CommunicationInbox) =>
    thread.unread > 0 && readThreadIds.has(thread.id);
  if (!threads.some(isUnreadAndRead)) return threads;

  return threads.map((thread) => (isUnreadAndRead(thread) ? { ...thread, unread: 0 } : thread));
}

/**
 * Merges refreshed threads into a list, replacing the ones with the same id and adding the rest.
 *
 * @param threads - the loaded threads.
 * @param updatedThreads - threads fetched again after a reply.
 * @returns the merged list, newest message first.
 */
export function upsertInboxThreads(
  threads: CommunicationInbox[],
  updatedThreads: CommunicationInbox[],
): CommunicationInbox[] {
  const updatedIds = new Set(updatedThreads.map((thread) => thread.id));
  return [...threads.filter((thread) => !updatedIds.has(thread.id)), ...updatedThreads].sort(
    compareInboxByLatestMessage,
  );
}

export const DEFAULT_INBOX_STATUS = "in-progress";

/**
 * Filters threads by status and by a search over the given fields, the same way for every list.
 *
 * @param threads - the threads to filter.
 * @param options.status - the status to keep; empty keeps in-progress threads.
 * @param options.search - matched case-insensitively against `searchFields`; empty keeps all.
 * @param options.searchFields - the text of a thread that the search looks in.
 * @returns the threads that match, in their original order.
 */
export function filterInboxThreads(
  threads: CommunicationInbox[],
  {
    status,
    search,
    searchFields,
  }: {
    status: string;
    search: string;
    searchFields: (thread: CommunicationInbox) => (string | undefined)[];
  },
): CommunicationInbox[] {
  const query = search.toLowerCase();
  const wantedStatus = status || DEFAULT_INBOX_STATUS;

  return threads.filter(
    (thread) =>
      thread.status === wantedStatus &&
      (!query || searchFields(thread).some((field) => field?.toLowerCase().includes(query))),
  );
}
